/**
 * NumberPlateInput Component
 *
 * Editable number plate input with three separate fields that mimics the visual design
 * of the NumberPlateVisual component. Supports continuous typing across fields with
 * automatic focus management.
 *
 * @module components/NumberPlateInput
 */

import { useRef } from "react"
// eslint-disable-next-line no-restricted-imports -- NumberPlateInput requires base TextInput for specialized multi-field number plate input
import { View, TextInput, StyleSheet, Image, ViewStyle, TextStyle } from "react-native"

import { colors } from "@/theme"
import { parsePlateIntoParts } from "@/utils/plateUtils"

export interface NumberPlateInputProps {
  /**
   * Full number plate value (e.g., "M-AB-1234E")
   */
  value: string

  /**
   * Callback when the plate value changes
   */
  onChangeValue: (value: string) => void

  /**
   * Whether the input has a validation error
   */
  hasError?: boolean

  /**
   * Size mode for the input
   */
  sizeMode?: "compact" | "normal"

  /**
   * Optional style override
   */
  style?: ViewStyle
}

/**
 * Validates a single field value against allowed characters
 */
function validateField(value: string, field: "city" | "letters" | "numbers"): string {
  if (field === "city") {
    // City code: 1-3 uppercase letters + umlauts
    return value
      .replace(/[^a-zA-ZäöüÄÖÜ]/g, "")
      .toUpperCase()
      .slice(0, 3)
  } else if (field === "letters") {
    // District letters: 0-2 uppercase letters + umlauts
    return value
      .replace(/[^a-zA-ZäöüÄÖÜ]/g, "")
      .toUpperCase()
      .slice(0, 2)
  } else {
    // Numbers: 1-4 digits + optional E/e suffix
    const cleaned = value.replace(/[^0-9eE]/g, "")
    // Extract digits and E separately
    const digits = cleaned.replace(/[eE]/g, "").slice(0, 4)
    const hasE = /[eE]/.test(cleaned)
    return digits + (hasE && digits.length > 0 ? "E" : "")
  }
}

/**
 * NumberPlateInput Component
 */
export function NumberPlateInput({
  value,
  onChangeValue,
  hasError = false,
  sizeMode = "normal",
  style,
}: NumberPlateInputProps) {
  const [city, letters, numbers] = parsePlateIntoParts(value)

  // Refs for focus management
  const cityRef = useRef<TextInput>(null)
  const lettersRef = useRef<TextInput>(null)
  const numbersRef = useRef<TextInput>(null)

  const plateFontFamily = "Kraftfahrzeugfont"

  // Size-specific adjustments (matching NumberPlateVisual)
  const baseFontSize = sizeMode === "compact" ? 10 : 22 // Reduced from 30 to 22
  const fontSize = baseFontSize
  const lineHeight = fontSize
  const paddingHorizontal = sizeMode === "compact" ? 2 : 4 // Reduced from 5 to 4
  const sealContainerWidth = fontSize * 1.5
  const stickerGapWidth = fontSize * 1.0

  /**
   * Handle change in city code field
   */
  const handleCityChange = (text: string) => {
    const validated = validateField(text, "city")
    const newValue = `${validated}-${letters}-${numbers}`.replace(/^-|-$/g, "")
    onChangeValue(newValue)

    // Auto-advance to letters field when max length reached
    if (validated.length === 3) {
      lettersRef.current?.focus()
    }
  }

  /**
   * Handle change in letters field
   */
  const handleLettersChange = (text: string) => {
    const validated = validateField(text, "letters")
    const newValue = `${city}-${validated}-${numbers}`.replace(/--/g, "-").replace(/^-|-$/g, "")
    onChangeValue(newValue)

    // Auto-advance to numbers field when max length reached
    if (validated.length === 2) {
      numbersRef.current?.focus()
    }
  }

  /**
   * Handle change in numbers field
   */
  const handleNumbersChange = (text: string) => {
    const validated = validateField(text, "numbers")
    const newValue = `${city}-${letters}-${validated}`.replace(/--/g, "-").replace(/^-|-$/g, "")
    onChangeValue(newValue)
  }

  /**
   * Handle backspace in letters field - go back to city if empty
   */
  const handleLettersKeyPress = (e: any) => {
    if (e.nativeEvent.key === "Backspace" && letters.length === 0) {
      cityRef.current?.focus()
    }
  }

  /**
   * Handle backspace in numbers field - go back to letters if empty
   */
  const handleNumbersKeyPress = (e: any) => {
    if (e.nativeEvent.key === "Backspace" && numbers.length === 0) {
      lettersRef.current?.focus()
    }
  }

  // Background colors - white for input version
  const plateBackgroundColor = colors.surface
  const inputBackgroundColor = colors.surface
  const textColor = colors.palette.neutral900
  const borderColor = hasError ? colors.primary : "rgba(228, 52, 43, 0.8)" // Red border

  return (
    <View style={[styles.wrapper, style]}>
      <View style={[styles.plateContainer, { backgroundColor: plateBackgroundColor, borderColor }]}>
        {/* Red Band with Logo */}
        <View style={styles.redBand}>
          <Image
            source={require("@assets/images/Logo white - transparent.png")}
            style={styles.redBandLogo}
            resizeMode="contain"
          />
        </View>

        {/* Input Fields Container */}
        <View style={[styles.inputAreaContainer, { backgroundColor: plateBackgroundColor }]}>
          {/* Part 1: City Code */}
          <TextInput
            ref={cityRef}
            style={[
              styles.inputField,
              styles.part1,
              {
                backgroundColor: inputBackgroundColor,
                color: textColor,
                fontFamily: plateFontFamily,
                fontSize: fontSize,
                lineHeight: lineHeight,
                paddingHorizontal: paddingHorizontal,
              } as TextStyle,
            ]}
            value={city}
            onChangeText={handleCityChange}
            placeholder="M"
            placeholderTextColor={colors.palette.neutral300}
            maxLength={3}
            autoCapitalize="characters"
            autoComplete="off"
            autoCorrect={false}
          />

          {/* Seal and Dot */}
          <View style={[styles.sealContainer, { width: sealContainerWidth }]}>
            <Image
              source={require("@assets/images/Plakette.png")}
              style={styles.sealImage}
              resizeMode="contain"
            />
            <View style={styles.sealDot} />
          </View>

          {/* Part 2: Letters */}
          <TextInput
            ref={lettersRef}
            style={[
              styles.inputField,
              styles.part2,
              {
                backgroundColor: inputBackgroundColor,
                color: textColor,
                fontFamily: plateFontFamily,
                fontSize: fontSize,
                lineHeight: lineHeight,
                paddingHorizontal: paddingHorizontal,
              } as TextStyle,
            ]}
            value={letters}
            onChangeText={handleLettersChange}
            onKeyPress={handleLettersKeyPress}
            placeholder="AB"
            placeholderTextColor={colors.palette.neutral300}
            maxLength={2}
            autoCapitalize="characters"
            autoComplete="off"
            autoCorrect={false}
          />

          {/* Sticker Gap */}
          <View style={[styles.stickerGap, { width: stickerGapWidth }]} />

          {/* Part 3: Numbers */}
          <TextInput
            ref={numbersRef}
            style={[
              styles.inputField,
              styles.part3,
              {
                backgroundColor: inputBackgroundColor,
                color: textColor,
                fontFamily: plateFontFamily,
                fontSize: fontSize,
                lineHeight: lineHeight,
                paddingHorizontal: paddingHorizontal,
              } as TextStyle,
            ]}
            value={numbers}
            onChangeText={handleNumbersChange}
            onKeyPress={handleNumbersKeyPress}
            placeholder="1234"
            placeholderTextColor={colors.palette.neutral300}
            maxLength={5}
            autoComplete="off"
            autoCorrect={false}
            keyboardType="default"
          />
        </View>
      </View>
    </View>
  )
}

// Styles matching NumberPlateVisual exactly
const styles = StyleSheet.create({
  inputAreaContainer: {
    alignItems: "center",
    backgroundColor: colors.surface,
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 0,
  },
  inputField: {
    alignSelf: "center",
    backgroundColor: colors.surface,
    borderColor: colors.palette.neutral300,
    borderRadius: 6,
    borderWidth: 1,
    color: colors.palette.neutral900,
    paddingVertical: 8,
    textAlign: "center",
  },
  part1: {
    flex: 0.25,
    marginLeft: 4,
    maxWidth: 70,
    minWidth: 50,
  },
  part2: {
    flex: 0.18,
    maxWidth: 55,
    minWidth: 40,
  },
  part3: {
    flex: 0.32,
    marginRight: 4,
    maxWidth: 95,
    minWidth: 70,
  },
  plateContainer: {
    width: "100%",
    height: "100%",
    backgroundColor: colors.surface,
    borderWidth: 2,
    borderColor: "rgba(228, 52, 43, 0.8)", // Red border
    borderRadius: 8,
    flexDirection: "row",
    alignItems: "stretch",
    shadowColor: colors.palette.neutral900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    overflow: "hidden",
  },
  redBand: {
    alignItems: "center",
    backgroundColor: colors.primary,
    justifyContent: "center",
    width: "9.6%",
  },
  redBandLogo: {
    height: "70%",
    width: "70%",
  },
  sealContainer: {
    alignItems: "center",
    flexDirection: "column",
    height: "100%",
    justifyContent: "space-around",
  },
  sealDot: {
    aspectRatio: 1,
    backgroundColor: colors.palette.neutral300,
    borderRadius: 500,
    height: "30%",
    width: "30%",
  },
  sealImage: {
    aspectRatio: 1,
    height: "30%",
    width: "30%",
  },
  stickerGap: {
    alignItems: "center",
    height: "100%",
    justifyContent: "center",
  },
  wrapper: {
    alignItems: "center",
    aspectRatio: 520 / 110, // Authentic German plate aspect ratio
    justifyContent: "center",
    width: "100%",
  },
})
